/**
 * ============================================
 * FRONTEND AUTH API CLIENT
 * ============================================
 */

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'writer' | 'authorizer' | 'admin' | 'attendee' | 'contestant' | 'volunteer' | 'community_head' | 'loc_member' | 'cwc_member' | 'media';
  avatar?: string;
  photoUrl?: string;
  emailVerified: boolean;
  isDiaspora: boolean;
  title?: string;
  department?: string;
}

export interface AuthResponse {
  success: boolean;
  user?: AuthUser;
  accessToken?: string;
  refreshToken?: string;
  error?: string;
  requiresVerification?: boolean;
}

const API_BASE = '/api/auth';

export async function register(data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  villageOfOrigin?: string;
  isDiaspora?: boolean;
}): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  return response.json();
}

export async function logout(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/logout`, {
    method: 'POST',
    credentials: 'include',
  });
  return response.json();
}

export async function getCurrentUser(): Promise<{ user?: AuthUser; error?: string }> {
  const response = await fetch(`${API_BASE}/me`, { credentials: 'include' });
  if (!response.ok) return { error: 'Not authenticated' };
  return response.json();
}

export async function refreshToken(): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/refresh`, {
    method: 'POST',
    credentials: 'include',
  });
  return response.json();
}

export function loginWithGoogle(): void {
  window.location.href = `${API_BASE}/google`;
}

export function loginWithFacebook(): void {
  window.location.href = `${API_BASE}/facebook`;
}

export async function forgotPassword(email: string) {
  const response = await fetch(`${API_BASE}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return response.json();
}

export async function resetPassword(token: string, newPassword: string) {
  const response = await fetch(`${API_BASE}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  return response.json();
}

export async function verifyEmail(email: string, code: string) {
  const response = await fetch(`${API_BASE}/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  return response.json();
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const response = await fetch(`${API_BASE}/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return response.json();
}
